// Exportador universal Excel (.xlsx) y PDF.
// Uso: exportToExcel({ title, filters, columns, rows, filename })
//      exportToPdf({ title, filters, columns, rows, filename, landscape })
//
// - columns: [{ key, label, width, type: 'text'|'money'|'date'|'number' }]
// - rows: array de objetos con las keys de columns
// - filters: objeto con {clave: valor} que se muestran en el encabezado
// - filename: sin extensión

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GT_TZ = "America/Guatemala";

function fmtValue(v, type) {
  if (v == null || v === "") return "";
  if (type === "money") {
    const n = Number(v || 0);
    return `Q ${n.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (type === "number") return Number(v || 0);
  if (type === "date") {
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    try {
      return new Intl.DateTimeFormat("es-GT", {
        year: "numeric", month: "2-digit", day: "2-digit", timeZone: GT_TZ,
      }).format(new Date(v));
    } catch { return v; }
  }
  return String(v);
}

export function exportToExcel({ title = "Reporte", filters = {}, columns = [], rows = [], filename = "reporte" }) {
  const wb = XLSX.utils.book_new();
  // Metadata rows
  const meta = [
    [title],
    [],
    ...(Object.entries(filters).map(([k, v]) => [`${k}:`, String(v || "—")])),
    [`Generado:`, new Intl.DateTimeFormat("es-GT", {
      dateStyle: "long", timeStyle: "short", timeZone: GT_TZ,
    }).format(new Date())],
    [],
  ];
  const headers = columns.map(c => c.label);
  const dataRows = rows.map(r => columns.map(c => {
    const v = r[c.key];
    if (c.type === "money" || c.type === "number") return Number(v || 0);
    return fmtValue(v, c.type);
  }));

  // Fila de totales (para columnas money/number)
  const totalsRow = columns.map((c, i) => {
    if (c.type === "money" || c.type === "number") {
      return rows.reduce((s, r) => s + Number(r[c.key] || 0), 0);
    }
    return i === 0 ? "TOTAL" : "";
  });

  const aoa = [...meta, headers, ...dataRows, totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  ws["!cols"] = columns.map(c => ({ wch: c.width || Math.max(c.label.length + 2, 14) }));
  // Freeze primera fila de datos
  ws["!freeze"] = { xSplit: 0, ySplit: meta.length + 1 };
  // Format money cells
  const headerRowIdx = meta.length; // 0-indexed
  columns.forEach((c, cIdx) => {
    if (c.type !== "money") return;
    dataRows.forEach((_, rIdx) => {
      const cell = XLSX.utils.encode_cell({ r: headerRowIdx + 1 + rIdx, c: cIdx });
      if (ws[cell]) ws[cell].z = "\"Q \"#,##0.00";
    });
    // Total row
    const totalCell = XLSX.utils.encode_cell({ r: headerRowIdx + 1 + dataRows.length, c: cIdx });
    if (ws[totalCell]) ws[totalCell].z = "\"Q \"#,##0.00";
  });

  // Auto-filter en la fila de encabezados
  const lastCol = XLSX.utils.encode_col(columns.length - 1);
  ws["!autofilter"] = { ref: `A${headerRowIdx + 1}:${lastCol}${headerRowIdx + 1}` };

  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportToPdf({ title = "Reporte", filters = {}, columns = [], rows = [], filename = "reporte", landscape = true }) {
  const doc = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "pt",
    format: "letter",
  });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(16); doc.setFont(undefined, "bold");
  doc.text(title, 40, 40);
  doc.setFontSize(9); doc.setFont(undefined, "normal");
  let y = 60;
  const filtersTxt = Object.entries(filters).map(([k, v]) => `${k}: ${v || "—"}`).join("  ·  ");
  if (filtersTxt) {
    doc.text(filtersTxt, 40, y);
    y += 14;
  }
  const gen = new Intl.DateTimeFormat("es-GT", { dateStyle: "long", timeStyle: "short", timeZone: GT_TZ }).format(new Date());
  doc.text(`Generado: ${gen}`, 40, y);
  y += 8;

  // Table
  const head = [columns.map(c => c.label)];
  const body = rows.map(r => columns.map(c => fmtValue(r[c.key], c.type)));
  // Totales
  const foot = [columns.map((c, i) => {
    if (c.type === "money" || c.type === "number") {
      const s = rows.reduce((a, r) => a + Number(r[c.key] || 0), 0);
      return c.type === "money" ? `Q ${s.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : s;
    }
    return i === 0 ? "TOTAL" : "";
  })];

  autoTable(doc, {
    head, body, foot,
    startY: y + 10,
    styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.3, lineColor: [180, 180, 180] },
    headStyles: { fillColor: [0, 47, 167], textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [230, 230, 230], textColor: 0, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    didDrawPage: (data) => {
      const pageNum = doc.getCurrentPageInfo().pageNumber;
      const totalPages = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(`Página ${pageNum} / ${totalPages}`, pageWidth - 90, doc.internal.pageSize.getHeight() - 20);
    },
  });

  doc.save(`${filename}.pdf`);
}
